import React, { useEffect, useRef, useState, useCallback } from 'react';
import './EndrollPage.css';
import ContactForm from '../ContactFormStudent/ContactForm';
import ContactPage from '../Contactpage/ContactPage';

/* ── Modal ── */
const Modal = ({ open, onClose, title, accentClass, children }) => {
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  if (!open) return null;
  return (
    <div className="ep_modal_backdrop" onClick={onClose} role="dialog" aria-modal="true">
      <div className={`ep_modal_box ${accentClass}`} onClick={e => e.stopPropagation()}>
        <div className="ep_modal_header">
          <h3 className="ep_modal_title">{title}</h3>
          <button className="ep_modal_close" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <div className={`ep_modal_divider ${accentClass === 'ep_modal_box--gold' ? 'ep_modal_divider--gold' : ''}`} />
        <div className="ep_modal_body">{children}</div>
      </div>
    </div>
  );
};

/* ── Parallax hook ── */
const useParallax = (speed = 0.35) => {
  const ref = useRef(null);
  const [offset, setOffset] = useState(0);

  const onScroll = useCallback(() => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const center = rect.top + rect.height / 2 - window.innerHeight / 2;
    setOffset(center * speed);
  }, [speed]);

  useEffect(() => {
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll(); // initial call
    return () => window.removeEventListener('scroll', onScroll);
  }, [onScroll]);

  return [ref, offset];
};

/* ── useInView hook ── */
const useInView = (threshold = 0.15) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
};

const IMAGES = [
  "https://i.pinimg.com/736x/72/8d/fe/728dfeff858ccdb8868c9f6ef45124da.jpg",
  "https://img.freepik.com/premium-photo/pink-ribbons-stethoscope-pastel-background-symbol-womens-breast-cancer-awareness_53476-6136.jpg",
];

const WHO_CAN = [
  { icon: '🧬', text: 'Life science graduates' },
  { icon: '🩺', text: 'Healthcare professionals' },
  { icon: '🎓', text: 'Freshers seeking a career in healthcare administration' },
  { icon: '💼', text: 'Working professionals aiming for a career shift' },
  { icon: '📋', text: 'Anyone interested in medical coding & billing' },
  { icon: '🌍', text: 'Students looking for global healthcare career opportunities' },
];

const EndrollPage = () => {
  const [heroRef, heroOffset] = useParallax(0.3);
  const [currentImage, setCurrentImage] = useState(0);
  const [titleRef, titleVis] = useInView();
  const [whoRef,   whoVis]   = useInView();
  const [formsRef, formsVis] = useInView(0.08);
  const [studentOpen, setStudentOpen] = useState(false);
  const [clientOpen,  setClientOpen]  = useState(false);

  useEffect(() => {
    const t = setInterval(() => setCurrentImage(p => (p + 1) % IMAGES.length), 4500);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="ep_root" id="endroll">

      {/* BG decorations */}
      <div className="ep_grid_bg" />
      <div className="ep_orb ep_orb--1" />
      <div className="ep_orb ep_orb--2" />
      <div className="ep_orb ep_orb--3" />

      {/* ══ WHO CAN ENROLL — முதல்ல ══ */}
      <section className="ep_who_section" ref={whoRef}>
        <div className="ep_inner">
          <div className={`ep_section_header ${whoVis ? 'ep_anim--fadeUp' : ''}`}>
            <span className="ep_tag">Open Enrollment</span>
            <h2 className="ep_section_title">Who Can Enroll?</h2>
            <div className="ep_title_bar" />
          </div>
          <div className="ep_who_grid">
            {WHO_CAN.map((item, i) => (
              <div
                key={i}
                className={`ep_who_card ${whoVis ? 'ep_anim--cardIn' : ''}`}
                style={{ '--card-delay': `${i * 0.1}s` }}
              >
                <div className="ep_who_card_glow" />
                <span className="ep_who_icon">{item.icon}</span>
                <p className="ep_who_text">{item.text}</p>
                <div className="ep_who_card_bar" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ HERO BANNER — Who Can Enroll-க்கு கீழே ══ */}
      <section className="ep_hero" ref={heroRef}>
        {/*
          Parallax wrapper:
          - height is made taller than the section (overflows, clipped by overflow:hidden on .ep_hero)
          - translateY shifts based on scroll position relative to viewport center
        */}
        <div
          className="ep_hero_parallax"
          style={{ transform: `translateY(${heroOffset}px)` }}
        >
          {IMAGES.map((src, i) => (
            <div
              key={i}
              className={`ep_hero_img ${i === currentImage ? 'ep_hero_img--active' : ''}`}
              style={{ backgroundImage: `url(${src})` }}
            />
          ))}
        </div>

        <div className="ep_hero_overlay" />

        <div className="ep_hero_content" ref={titleRef}>
          <h1 className={`ep_hero_title ${titleVis ? 'ep_anim--fadeUp' : ''}`}>
            ENROLL <span className="ep_hero_title--accent">NOW</span>
          </h1>
          <p className={`ep_hero_sub ${titleVis ? 'ep_anim--fadeUp ep_anim--d2' : ''}`}>
            Take the first step toward a thriving career in healthcare administration &amp; medical coding.
          </p>
        </div>

        <div className="ep_dots">
          {IMAGES.map((_, i) => (
            <button
              key={i}
              className={`ep_dot ${i === currentImage ? 'ep_dot--active' : ''}`}
              onClick={() => setCurrentImage(i)}
              aria-label={`Slide ${i + 1}`}
            />
          ))}
        </div>
      </section>

      {/* ══ FORMS SECTION ══ */}
      <section className="ep_forms_section" ref={formsRef}>
        <div className="ep_inner">
          <div className={`ep_section_header ${formsVis ? 'ep_anim--fadeUp' : ''}`}>
            <span className="ep_tag ep_tag--gold">Get In Touch</span>
            <h2 className="ep_section_title">Register Today</h2>
            <div className="ep_title_bar ep_title_bar--gold" />
          </div>
          <div className="ep_forms_grid">

            {/* Student card */}
            <div className={`ep_slogan_card ep_slogan_card--teal ${formsVis ? 'ep_anim--slideLeft' : ''}`}>
              <div className="ep_slogan_card_glow ep_slogan_card_glow--teal" />
              <div className="ep_slogan_badge ep_slogan_badge--teal">🎓 For Student</div>
              <h3 className="ep_slogan_heading">
                Your Future in <span className="ep_slogan_heading--teal">Healthcare</span><br />Starts Here
              </h3>
              <p className="ep_slogan_text">
                "From classroom to clinic — master medical coding, earn global certifications, and step into a career that matters."
              </p>
              <div className="ep_slogan_features">
                <span className="ep_slogan_pill">✅ Expert Mentors</span>
                <span className="ep_slogan_pill">✅ Certification Ready</span>
                <span className="ep_slogan_pill">✅ Placement Support</span>
              </div>
              <button className="ep_slogan_btn ep_slogan_btn--teal" onClick={() => setStudentOpen(true)}>
                <span className="ep_slogan_btn_text">Apply Now</span>
                <span className="ep_slogan_btn_ripple" />
              </button>
            </div>

            {/* Client card */}
            <div className={`ep_slogan_card ep_slogan_card--gold ${formsVis ? 'ep_anim--slideRight' : ''}`}>
              <div className="ep_slogan_card_glow ep_slogan_card_glow--gold" />
              <div className="ep_slogan_badge ep_slogan_badge--gold">🏥 For Client</div>
              <h3 className="ep_slogan_heading">
                Precision Coding.<br /><span className="ep_slogan_heading--gold">Maximum Revenue.</span>
              </h3>
              <p className="ep_slogan_text">
                "Zero claim denials, 100% HIPAA compliance. Let our certified coders transform your revenue cycle."
              </p>
              <div className="ep_slogan_features">
                <span className="ep_slogan_pill ep_slogan_pill--gold">✅ HIPAA Compliant</span>
                <span className="ep_slogan_pill ep_slogan_pill--gold">✅ Zero Denials</span>
                <span className="ep_slogan_pill ep_slogan_pill--gold">✅ 24/7 Support</span>
              </div>
              <button className="ep_slogan_btn ep_slogan_btn--gold" onClick={() => setClientOpen(true)}>
                <span className="ep_slogan_btn_text">Partner With Us</span>
                <span className="ep_slogan_btn_arrow">→</span>
                <span className="ep_slogan_btn_ripple" />
              </button>
            </div>

          </div>
        </div>
      </section>

      {/* Modals */}
      <Modal open={studentOpen} onClose={() => setStudentOpen(false)}
        title="Student Enrollment Form" accentClass="ep_modal_box--teal">
        <ContactForm />
      </Modal>
      <Modal open={clientOpen} onClose={() => setClientOpen(false)}
        title="Client Partnership Form" accentClass="ep_modal_box--gold">
        <ContactPage />
      </Modal>

    </div>
  );
};

export default EndrollPage;